/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, no-prototype-builtins, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends from
import { set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { isArray } from '@ember/array';
import { get, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { TrackedArray } from 'tracked-built-ins';
// Internal Imports
import BulkImportContactProp from 'linced-app/enums/bulk-import-contact-prop';
import BulkImportEnrolmentProp from 'linced-app/enums/bulk-import-enrolment-prop';
import BulkImportOutcome from 'linced-app/enums/bulk-import-outcome';
import BulkImportSource from 'linced-app/enums/bulk-import-source';
import BulkImportStatus from 'linced-app/enums/bulk-import-status';
import BulkImportType from 'linced-app/enums/bulk-import-type';
import ContactType from 'linced-app/enums/contact-type';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import MedicalConditionType from 'linced-app/enums/medical-condition-type';
import NzMoeActivity from 'linced-app/enums/nz-moe-post-school-activity';
import NzMoeDepartureReason from 'linced-app/enums/nz-moe-departure-reason';
import {
  conformDate,
  isTruthy,
  toCamelCase,
} from '@linc-technologies/linced-common/utils/strings';
import { conformFieldValue } from 'linced-app/utils/fields';
// External Imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import MedicalRelationship from '@linc-technologies/linced-common/enums/medical-relationship';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Relationship from '@linc-technologies/linced-common/enums/contact-relationship';
import NzMoeSchool from '@linc-technologies/linced-common/enums/nz-moe-school';
import Papa from 'papaparse';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import {
  compact,
  filterBy,
  findBy,
  mapBy,
  rejectBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';

const alternateNames = {
  1: {
    legalFirstName: 'First Name (Legal)',
    legalLastName: 'Last Name (Legal)',
    legalSurname: 'Last Name (Legal)',
    preferredFirstName: 'First Name (Preferred)',
    preferredLastName: 'Last Name (Preferred)',
    firstName: 'First Name (Legal)',
    lastName: 'Last Name (Legal)',
    surname: 'Last Name (Preferred)',
    nationalStudentNumber: 'National Student Number (NSN)',
    nsn: 'National Student Number (NSN)',
    preEnrol: 'Is Pre-Enrolled',
    preEnrolled: 'Is Pre-Enrolled',
    isPreEnrol: 'Is Pre-Enrolled',
    isPreEnrolled: 'Is Pre-Enrolled',
    leaver: 'Is a former student',
    isLeaver: 'Is a former student',
    former: 'Is a former student',
    isFormer: 'Is a former student',
    hasLeft: 'Is a former student',
    vacated: 'Is a former student',
    immunisationCom: 'Immunisation Comment',
    address1: 'Address Line 1',
    address2: 'Address Line 2',
    address3: 'Address Line 3',
    address4: 'Post Code',
    phone: 'Home Phone Number',
    homePhone: 'Home Phone Number',
    mobile: 'Cellphone Number',
    mobilePhone: 'Cellphone Number',
    cellphone: 'Cellphone Number',
    verificationCriteria: 'Eligibility Criteria',
    verificationSerialNumber: 'Verification Document Serial Number',
    verificationExpiryDate: 'Verification Document Expiry Date',
    zoning: 'Zoning Status',
    ethnicOrigin: 'Ethnicities',
    ethnicOrigin1: 'Ethnicities',
    ethnicOrigin2: 'Ethnicities',
    ethnicOrigin3: 'Ethnicities',
    iwis: 'Iwi',
    iwi1: 'Iwi',
    iwi2: 'Iwi',
    iwi3: 'Iwi',
    iwi4: 'Iwi',
    iwi5: 'Iwi',
    firstAttendance: 'Start Date',
    lastAttendance: 'End Date',
    emailAddress: 'Student Email Address',
    tuitionFee:
      'Tuition Fee (International Foreign Fee-Paying Students - Weekly Fee EXCLUDING GST. Round to nearest dollar.)',
  },
  2: {
    surname: 'Last Name',
    address1: 'Address Line 1',
    address2: 'Address Line 2',
    address3: 'Address Line 3',
    address4: 'Address Line 4',
    postcode: 'Post Code',
    phone: 'Home Phone Number',
    homePhone: 'Home Phone Number',
    mobile: 'Mobile Phone Number',
    mobilePhone: 'Mobile Phone Number',
    cellphone: 'Mobile Phone Number',
    email: 'Email Address',
  },
  3: {
    surname: 'Last Name',
    address1: 'Address Line 1',
    address2: 'Address Line 2',
    address3: 'Address Line 3',
    address4: 'Address Line 4',
    postcode: 'Post Code',
    phone: 'Home Phone Number',
    homePhone: 'Home Phone Number',
    mobile: 'Mobile Phone Number',
    mobilePhone: 'Mobile Phone Number',
    cellphone: 'Mobile Phone Number',
    work: 'Work Phone Number',
    workPhone: 'Work Phone Number',
    email: 'Email Address',
    contactPriority: 'Priority',
    type: 'Relationship',
    notes: 'Contact Notes',
    comment: 'Contact Notes',
    sms: 'Receives SMS',
    liveswith: 'Lives with Student',
    job: 'Occupation',
    access: 'Allowed Access',
    voting: 'BOT Voting', // TODO: Jurisdictional alternate names?
    billPayer: 'Finance Access',
    isBillPayer: 'Finance Access',
    isABillPayerForLearner: 'Finance Access',
  },
  4: {
    surname: 'Last Name',
    address1: 'Address Line 1',
    address2: 'Address Line 2',
    address3: 'Address Line 3',
    address4: 'Address Line 4',
    postcode: 'Post Code',
    contactPriority: 'Priority',
    medicalCentreName: 'Medical Centre',
  },
};

const primaryContactFieldPattern =
  /^(?:Primary[ _]?)?(?:Contact|Caregiver)[ _]?(\d+)[ _]?(.*)$/i;
const emergencyContactFieldPattern =
  /^(?:(?:Emergency|Other|Additional)[ _]?)(?:Contact|Caregiver)?[ _]?(\d+)[ _]?(.*)$/i;
const medicalContactFieldPattern =
  /^Medical[ _]?(?:Contact|Specialist)[ _]?(\d+)[ _]?(.*)$/i;
const medicalConditionFieldPattern =
  /^Medical[ _]?Condition[ _]?(\d+)[ _]?(.*)$/i;
const studentPrefix = /^(school|student)[_ ]/i;

const contactProps = {
  priority: BulkImportContactProp.PRIORITY,
  relationship: BulkImportContactProp.RELATIONSHIP,
  contactNotes: BulkImportContactProp.NOTES,
  allowedAccess: BulkImportContactProp.ALLOWED_ACCESS,
  receivesSms: BulkImportContactProp.RECEIVES_SMS,
  financeAccess: BulkImportContactProp.BILL_PAYER,
  startDate: BulkImportContactProp.MEDICAL_START_DATE,
  endDate: BulkImportContactProp.MEDICAL_END_DATE,
  permissionToContact: BulkImportContactProp.PERMISSION_TO_CONTACT,
};

const medicalDateProperties = ['startDate', 'reviewDate', 'endDate'];

export default class IndexController extends Controller {
  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  @action
  setFile(value) {
    set(this, 'file', value);
  }

  queryParams = [
    {
      importType: 'type',
    },
  ];

  // Services
  @service
  features;

  @service
  permissions;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @service
  strings;

  @alias('session.school.jurisdiction')
  jurisdiction;

  BulkImportOutcome = BulkImportOutcome;
  ContactType = ContactType;
  PersonType = PersonType;
  Scopes = Scopes;
  @tracked
  currentStep = 0;
  @tracked importType = 'learners';
  @tracked
  file = null;
  fileTypes = Object.freeze(['text/csv', '.csv', '.hero']);

  // Extras for Hero file processing.
  @computed('file.extension')
  get isHeroFile() {
    if (isEmpty(this.file) || isEmpty(this.file.extension)) {
      return false;
    }
    return this.file.extension === 'hero';
  }

  @tracked
  incorrectSchool = false;
  @tracked
  heroFileCheckInProgress = false;

  checkSchoolId(results) {
    let importSchoolId = results.data[0][0].trim();
    let thisSchoolId = this.get('session.school.jurisdictionalId').trim();
    this.heroFileCheckInProgress = false;
    this.incorrectSchool = importSchoolId !== thisSchoolId;
  }

  @tracked
  heroFileLearnersWithoutIdCreated = [];

  enrolmentFields = Object.freeze([
    {
      key: 'startDate',
      id: 'startDate',
      camelCaseLabel: 'startDate',
      label: 'First attendance date',
      group: {
        name: 'Enrolment',
      },
    },
    {
      key: 'endDate',
      id: 'endDate',
      camelCaseLabel: 'endDate',
      label: 'Last attendance date',
      group: {
        name: 'Enrolment',
      },
    },
    {
      key: 'isPreEnrolled',
      id: 'isPreEnrolled',
      camelCaseLabel: 'isPreEnrolled',
      label: 'Is Pre-Enrolled',
      group: {
        name: 'Enrolment',
      },
    },
    {
      key: 'isFormer',
      id: 'isFormer',
      camelCaseLabel: 'isAFormerStudent',
      label: 'Is a former student',
      group: {
        name: 'Enrolment',
      },
    },
    {
      key: 'reasonForLeaving',
      id: 'reasonForLeaving',
      camelCaseLabel: 'reasonForLeaving',
      label: 'Reason for Leaving',
      group: {
        name: 'Enrolment',
      },
    },
    {
      key: 'destinationSchool',
      id: 'destinationSchool',
      camelCaseLabel: 'destinationSchool',
      label: 'Destination School',
      group: {
        name: 'Enrolment',
      },
    },
    {
      key: 'postSchoolActivity',
      id: 'postSchoolActivity',
      camelCaseLabel: 'postSchoolActivity',
      label: 'Post-school Activity',
      group: {
        name: 'Enrolment',
      },
    },
  ]);

  contactFields = Object.freeze([
    {
      key: 'priority',
      id: 'priority',
      camelCaseLabel: 'priority',
      label: 'Contact Priority',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.INTEGER,
      inputType: InputType.NUMBER,
      htmlAttributes: {
        min: 1,
        max: 9,
      },
      defaultValue: {
        valueInt: 9, // If not set, default to "last" so that it comes after any with an explicit (higher) priority.
      },
    },
    {
      key: 'relationship',
      id: 'relationship',
      camelCaseLabel: 'relationship',
      label: 'Relationship to learner',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.INTEGER,
    },
    {
      key: 'contactNotes',
      id: 'contactNotes',
      camelCaseLabel: 'contactNotes',
      label: 'Notes',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.STRING,
    },
    {
      key: 'allowedAccess',
      id: 'allowedAccess',
      camelCaseLabel: 'allowedAccess',
      label: 'Allowed access',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'receivesSms',
      id: 'receivesSms',
      camelCaseLabel: 'receivesSms',
      label: 'Receives Text Messages for learner',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'financeAccess',
      id: 'financeAccess',
      camelCaseLabel: 'financeAccess',
      label: 'Finance Access',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.BOOLEAN,
    },
  ]);

  medicalContactFields = Object.freeze([
    {
      key: 'priority',
      id: 'priority',
      camelCaseLabel: 'priority',
      label: 'Priority',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.INTEGER,
      inputType: InputType.NUMBER,
      htmlAttributes: {
        min: 1,
        max: 9,
      },
      defaultValue: {
        valueInt: 9, // If not set, default to "last" so that it comes after any with an explicit (higher) priority.
      },
    },
    {
      key: 'relationship',
      id: 'relationship',
      camelCaseLabel: 'type',
      label: 'Type',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.INTEGER,
    },
    {
      key: 'notes',
      id: 'notes',
      camelCaseLabel: 'studentSpecificNotes',
      label: 'Student Specific Notes',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.STRING,
    },
    {
      key: 'permissionToContact',
      id: 'permissionToContact',
      camelCaseLabel: 'permissionToContact',
      label: 'Permission to Contact',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'startDate',
      id: 'startDate',
      camelCaseLabel: 'startDate',
      label: 'Start Date',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.STRING,
    },
    {
      key: 'endDate',
      id: 'endDate',
      camelCaseLabel: 'endDate',
      label: 'End Date',
      group: {
        name: 'Relationship',
        sequence: 9,
      },
      dataType: DataType.STRING,
    },
  ]);

  @computed('model.learnerFields.[]')
  get medicalConditionFields() {
    let fields = [
      {
        key: 'type',
        id: 'type',
        camelCaseLabel: 'typeOfCondition',
        label: 'Type of Condition',
        dataType: DataType.INTEGER,
        inputType: InputType.SELECT,
        options: MedicalConditionType.values.map((option) => {
          // Add valueInt because the field's datatype is INTEGER.
          option.valueInt = option.value;
          return option;
        }),
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
      {
        key: 'priority',
        id: 'priority',
        camelCaseLabel: 'priority',
        label: 'Priority',
        dataType: DataType.INTEGER,
        defaultValue: {
          valueInt: 9, // If not set, default to "last" so that it comes after any with an explicit (higher) priority.
        },
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
      {
        key: 'description',
        id: 'description',
        camelCaseLabel: 'description',
        label: 'Description',
        dataType: DataType.STRING,
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
      {
        key: 'startDate',
        id: 'startDate',
        camelCaseLabel: 'startDate',
        label: 'Start Date',
        dataType: DataType.STRING,
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
      {
        key: 'reviewDate',
        id: 'reviewDate',
        camelCaseLabel: 'reviewDate',
        label: 'Review Date',
        dataType: DataType.STRING,
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
      {
        key: 'endDate',
        id: 'endDate',
        camelCaseLabel: 'endDate',
        label: 'End Date',
        dataType: DataType.STRING,
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
      {
        key: 'prescribedMedicationHeld',
        id: 'prescribedMedicationHeld',
        camelCaseLabel: 'prescribedMedicationHeldOnsite',
        label: 'Prescribed Medication Held Onsite',
        dataType: DataType.BOOLEAN,
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
      {
        key: 'anaphylaxis',
        id: 'anaphylaxis',
        camelCaseLabel: 'anaphylaxis',
        label: 'Anaphylaxis',
        dataType: DataType.BOOLEAN,
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
      {
        key: 'highRisk',
        id: 'highRisk',
        camelCaseLabel: 'highRisk',
        label: 'High Risk',
        dataType: DataType.BOOLEAN,
        group: {
          name: 'Medical Condition',
          sequence: 1,
        },
      },
    ];
    fields.push(
      ...this.get('model.learnerFields').filter(
        (item) => get(item, 'group.key') === 'medicalCondition',
      ),
    );
    return fields;
  }

  // contactPriorityPattern is a regular expression for extracting the contact priority from the key.
  contactPriorityPattern = /Contact (\d+)/i;

  @computed('contactFields.[]')
  get contactFieldKeys() {
    return this.contactFields.map((item) => item.key);
  }

  financeFields = Object.freeze([
    {
      key: 'accountSalutation',
      id: 'accountSalutation',
      camelCaseLabel: 'accountSalutation',
      label: 'Account Salutation',
      group: {
        name: 'Finance',
      },
    },
  ]);

  heroIdField = Object.freeze([
    {
      key: 'heroId',
      id: 'heroId',
      camelCaseLabel: 'heroId',
      label: 'Hero ID',
      group: {
        name: 'Learner',
      },
    },
  ]);

  @computed('model.learnerFields.[]')
  get learnerFields() {
    return this.get('model.learnerFields').filter(
      (item) => get(item, 'group.key') !== 'medicalCondition',
    );
  }

  @computed(
    'importType',
    'model.{caregiverFields.[],staffFields.[]}',
    'learnerFields',
    'medicalFields',
    'medicalConditionFields',
    'features.pupilBillingEnabled',
    'isHeroFile',
  )
  get allFields() {
    let map;
    switch (this.importType) {
      case 'learners':
        map = {
          Learner: this.wrapFields(
            this.learnerFields,
            'Learner',
            this.isHeroFile ? [] : this.heroIdField,
          ),
          Enrolment: this.wrapFields(this.enrolmentFields, 'Enrolment'),
          'Contact 1': this.wrapFields(
            this.model.caregiverFields,
            'Contact 1',
            this.contactFields,
          ),
          'Contact 2': this.wrapFields(
            this.model.caregiverFields,
            'Contact 2',
            this.contactFields,
          ),
          'Contact 3': this.wrapFields(
            this.model.caregiverFields,
            'Contact 3',
            this.contactFields,
          ),
          'Contact 4': this.wrapFields(
            this.model.caregiverFields,
            'Contact 4',
            this.contactFields,
          ),
          'Emergency Contact 1': this.wrapFields(
            this.model.caregiverFields,
            'Emergency Contact 1',
            this.contactFields,
          ),
          'Emergency Contact 2': this.wrapFields(
            this.model.caregiverFields,
            'Emergency Contact 2',
            this.contactFields,
          ),
          'Emergency Contact 3': this.wrapFields(
            this.model.caregiverFields,
            'Emergency Contact 3',
            this.contactFields,
          ),
          'Emergency Contact 4': this.wrapFields(
            this.model.caregiverFields,
            'Emergency Contact 4',
            this.contactFields,
          ),
          'Medical Contact 1': this.wrapFields(
            this.medicalFields,
            'Medical Contact 1',
            this.medicalContactFields,
          ),
          'Medical Contact 2': this.wrapFields(
            this.medicalFields,
            'Medical Contact 2',
            this.medicalContactFields,
          ),
          'Medical Contact 3': this.wrapFields(
            this.medicalFields,
            'Medical Contact 3',
            this.medicalContactFields,
          ),
          'Medical Contact 4': this.wrapFields(
            this.medicalFields,
            'Medical Contact 4',
            this.medicalContactFields,
          ),
          'Medical Condition 1': this.wrapFields(
            this.medicalConditionFields,
            'Medical Condition 1',
          ),
          'Medical Condition 2': this.wrapFields(
            this.medicalConditionFields,
            'Medical Condition 2',
          ),
          'Medical Condition 3': this.wrapFields(
            this.medicalConditionFields,
            'Medical Condition 3',
          ),
          'Medical Condition 4': this.wrapFields(
            this.medicalConditionFields,
            'Medical Condition 4',
          ),
        };
        if (this.features.pupilBillingEnabled) {
          map['Finance'] = this.wrapFields(this.financeFields, 'Finance');
        }
        return map;
      case 'staff':
        return {
          Staff: this.wrapFields(this.model.staffFields, 'Staff'),
        };
    }

    return {};
  }

  @computed('model.medicalFields.@each.key')
  get medicalFields() {
    return this.model.medicalFields.filter((field) => {
      return !(
        field.key.match(/:address4$/) || field.key.includes(':postcode')
      );
    });
  }

  wrapFields(fields, key, extraFields) {
    let wrapped = fields.map((field) => {
      return {
        key,
        field,
      };
    });

    if (isArray(extraFields) && extraFields.length > 0) {
      wrapped.push(
        ...extraFields.map((field) => {
          return {
            key,
            field,
          };
        }),
      );
    }

    return wrapped;
  }

  @tracked
  processingColumns = false;

  columnsProcessed(results) {
    if (this.isHeroFile) {
      // Drop the first row for Hero files, as it should only contain the school's jurisdictional ID,
      // and has already been checked by this point.
      results.data.shift();
    }
    this.preview = results;
  }

  @computed('importType', 'preview.data.[]')
  get defaultColumnMap() {
    let destinations = [];
    if (isEmpty(this.preview)) {
      return destinations;
    }
    switch (this.importType) {
      case 'learners':
        this.preview.data[0].forEach((col, index) => {
          let primaryChunks = primaryContactFieldPattern.exec(col);
          let emergencyChunks = emergencyContactFieldPattern.exec(col);
          let medicalChunks = medicalContactFieldPattern.exec(col);
          let medicalConditionChunks = medicalConditionFieldPattern.exec(col);
          let destination;

          if (!isEmpty(primaryChunks)) {
            let pos = primaryChunks[1];
            let col = primaryChunks[2].trim();
            destination = this.findPotentialMatch(
              PersonType.CAREGIVER,
              `Contact ${pos}`,
              col,
              this.model.caregiverFields,
              this.contactFields,
            );
          } else if (!isEmpty(emergencyChunks)) {
            let pos = emergencyChunks[1];
            let col = emergencyChunks[2].trim();
            destination = this.findPotentialMatch(
              PersonType.CAREGIVER,
              `Emergency Contact ${pos}`,
              col,
              this.model.caregiverFields,
              this.contactFields,
            );
          } else if (!isEmpty(medicalChunks)) {
            let pos = medicalChunks[1];
            let col = medicalChunks[2].trim();
            destination = this.findPotentialMatch(
              PersonType.DOCTORS_AND_SPECIALISTS,
              `Medical Contact ${pos}`,
              col,
              this.model.medicalFields,
              this.medicalContactFields,
            );
          } else if (!isEmpty(medicalConditionChunks)) {
            let pos = medicalConditionChunks[1];
            let col = medicalConditionChunks[2].trim();
            destination = this.findPotentialMatch(
              null,
              `Medical Condition ${pos}`,
              col,
              this.medicalConditionFields,
              null,
            );
          } else {
            // Learner fields - check enrolment fields separately so that they don't get sucked into Learner
            // as if they do, they won't be removed from available fields or used properly in processing.
            let cleanCol = col.trim().replace(studentPrefix, '');
            // Check Enrolment fields first, in case there are custom fields which conflict with them.
            destination = this.findPotentialMatch(
              PersonType.LEARNER,
              'Enrolment',
              cleanCol,
              this.enrolmentFields,
            );
            if (isEmpty(destination) || destination.length === 0) {
              destination = this.findPotentialMatch(
                PersonType.LEARNER,
                'Learner',
                cleanCol,
                this.model.learnerFields,
              );
            }
            if (
              this.features.pupilBillingEnabled &&
              (isEmpty(destination) || destination.length === 0)
            ) {
              destination = this.findPotentialMatch(
                PersonType.LEARNER,
                'Finance',
                cleanCol,
                this.financeFields,
              );
            }
          }
          if (!isEmpty(destination)) {
            destination.autoMapped = true;
          }
          destination.columnIndex = index;
          destinations.push(destination);
        });
        break;

      case 'staff':
        this.preview.data[0].forEach((col, index) => {
          let destination = this.findPotentialMatch(
            PersonType.STAFF,
            'Staff',
            col.trim(),
            this.model.staffFields,
          );
          destination.columnIndex = index;
          destinations.push(destination);
        });
        break;
    }
    return destinations;
  }

  @computed('defaultColumnMap.@each.autoMapped')
  get numAutoMapped() {
    return this.defaultColumnMap.filter((item) => item.autoMapped).length;
  }

  hideAutoMapped = true;
  @tracked
  columnMap = [];

  @computed('columnMap.[]')
  get usedKeys() {
    return uniq(compact(this.columnMap).map((col) => col.key));
  }

  @computed('model.learnerFields.@each.inputType')
  get jurisdictionalIdField() {
    return this.model.learnerFields.find(
      (item) => item.inputType === InputType.JURISDICTIONAL_ID,
    );
  }

  @computed('jurisdictionalIdField', 'columnMap.[]')
  get jurisdictionalIdIndex() {
    if (isEmpty(this.columnMap)) {
      return false;
    }
    let compactColumns = compact(this.columnMap);
    let matchingColumn = compactColumns.find(
      (item) => item.field.key === this.jurisdictionalIdField.key,
    );

    if (isEmpty(matchingColumn)) {
      return false;
    }

    return matchingColumn.index;
  }

  @computed('columnMap.[]')
  get heroIdIndex() {
    if (isEmpty(this.columnMap)) {
      return false;
    }
    let compactColumns = compact(this.columnMap);
    let matchingColumn = compactColumns.find(
      (item) => item.field.key === 'heroId',
    );

    if (isEmpty(matchingColumn)) {
      return false;
    }
    return matchingColumn.index;
  }

  @computed('jurisdictionalIdField', 'columnMap.[]')
  get jurisdictionalIdNotMapped() {
    // Return true if no Jurisdictional ID field was found, or it isn't currently mapped.
    if (isEmpty(this.jurisdictionalIdField)) {
      return true;
    }
    if (isEmpty(this.columnMap)) {
      return true;
    }
    let usedFields = uniq(
      mapBy(filterBy(compact(this.columnMap), 'key', 'Learner'), 'field.key'),
    );
    return !usedFields.includes(this.jurisdictionalIdField.key);
  }

  @computed('columnMap.[]')
  get heroIdNotMapped() {
    // Return true if nothing is mapped yet
    if (isEmpty(this.columnMap)) {
      return true;
    }
    let usedFields = uniq(
      mapBy(filterBy(compact(this.columnMap), 'key', 'Learner'), 'field.key'),
    );
    return !usedFields.includes('heroId');
  }

  @computed('learnerMap.[]', 'contactMap.[]')
  get addressWarnings() {
    let warnings = [];
    for (let key of Object.keys(this.contactMap)) {
      let count = uniq(mapBy(this.contactMap[key], 'field.key')).reduce(
        (count, field) => {
          let isRequiredAddressField = !isEmpty(field.match(/:address[1-3]$/));
          if (isRequiredAddressField || field.includes(':postcode')) {
            count++;
          }
          return count;
        },
        0,
      );
      if (count > 0 && count < 4) {
        // Only push address warnings for non-medical contacts
        if (!key.startsWith('Medical Contact')) {
          warnings.push({ personType: key, missingCount: 4 - count });
        }
      }
    }
    let usedFields = uniq(this.learnerMap.map((item) => item.field.key));
    let count = usedFields.reduce((count, field) => {
      if (field.includes(':address') || field.includes(':postcode')) {
        count++;
      }
      return count;
    }, 0);
    if (count > 0 && count < 4) {
      warnings.push({ personType: 'Learner', missingCount: 4 - count });
    }
    return warnings;
  }

  @computed('model.staffFields.@each.key')
  get staffEmailField() {
    return this.model.staffFields.find(
      (item) => item.key === 'core:emailAddress',
    );
  }

  @computed('staffEmailField', 'compactMap.[]')
  get staffEmailIndex() {
    if (isEmpty(this.staffEmailField) || isEmpty(this.compactMap)) {
      return false;
    }
    return this.compactMap.find(
      (item) => item.field.key === this.staffEmailField.key,
    ).index;
  }

  @computed('staffEmailField', 'compactMap.[]')
  get staffEmailNotMapped() {
    // Return true if no Email Address field was found, or it isn't currently mapped.
    if (isEmpty(this.staffEmailField)) {
      return true;
    }
    if (isEmpty(this.compactMap)) {
      return true;
    }
    let usedFields = uniq(this.compactMap.map((item) => item.field.key));
    return !usedFields.includes(this.staffEmailField.key);
  }

  @computed('columnMap.[]')
  get staffMap() {
    return compact(this.columnMap);
  }

  @computed('columnMap.[]')
  get compactMap() {
    return rejectBy(
      compact(this.columnMap),
      'field.inputType',
      InputType.JURISDICTIONAL_ID,
    );
  }

  @computed('compactMap.[]')
  get learnerMap() {
    return this.compactMap.filter((item) => item.key === 'Learner');
  }

  @computed('compactMap.[]')
  get enrolmentMap() {
    return this.compactMap.filter((item) => item.key === 'Enrolment');
  }

  @computed('enrolmentMap.[]')
  get preEnrolField() {
    return this.enrolmentMap.find((item) => item.field.key === 'isPreEnrolled');
  }

  @computed('preEnrolField', 'enrolmentMap.[]')
  get preEnrolIndex() {
    if (isEmpty(this.preEnrolField)) {
      return false;
    }
    return this.preEnrolField.index;
  }

  @computed('enrolmentMap.[]')
  get formerField() {
    return this.enrolmentMap.find((item) => item.field.key === 'isFormer');
  }

  @computed('formerField', 'enrolmentMap.[]')
  get formerIndex() {
    if (isEmpty(this.formerField)) {
      return false;
    }
    return this.formerField.index;
  }

  @computed('preEnrolIndex', 'formerIndex')
  get hasStatusColumn() {
    return this.preEnrolIndex !== false || this.formerIndex !== false;
  }

  @computed('enrolmentMap.[]')
  get startDateField() {
    return this.enrolmentMap.find((item) => item.field.key === 'startDate');
  }

  @computed('enrolmentMap.[]')
  get endDateField() {
    return this.enrolmentMap.find((item) => item.field.key === 'endDate');
  }

  @computed('compactMap.[]')
  get contactMap() {
    let learnerMaps = ['Learner', 'Enrolment', 'Finance'];
    return this.compactMap
      .filter(
        (col) =>
          !learnerMaps.includes(col.key) &&
          !col.key.startsWith('Medical Contact'),
      )
      .reduce((map, column) => {
        if (isEmpty(map[column.key])) {
          map[column.key] = [];
        }
        map[column.key].push(column);
        return map;
      }, {});
  }

  @computed('contactMap')
  get contactsWithNotes() {
    return Object.keys(this.contactMap).reduce((contacts, key) => {
      if (findBy(this.contactMap[key], 'field.key', 'contactNotes')) {
        contacts.push(key);
      }
      return contacts;
    }, []);
  }

  @computed('contactMap')
  get contactsWithFinanceAccessFlag() {
    return Object.keys(this.contactMap).reduce((contacts, key) => {
      if (findBy(this.contactMap[key], 'field.key', 'financeAccess')) {
        contacts.push(key);
      }
      return contacts;
    }, []);
  }

  @computed('contactsWithFinanceAccessFlag.length')
  get hasFinanceAccessFlag() {
    return (this.get('contactsWithFinanceAccessFlag.length') ?? 0) > 0;
  }

  @computed('compactMap.[]')
  get financeMap() {
    return this.compactMap.filter((item) => item.key === 'Finance');
  }

  @computed('financeMap.[]')
  get accountSalutationField() {
    return this.financeMap.find(
      (item) => item.field.key === 'accountSalutation',
    );
  }

  @computed('accountSalutationField')
  get hasAccountSalutationField() {
    return !isEmpty(this.accountSalutationField);
  }

  @computed('compactMap.[]')
  get medicalContactMap() {
    return this.compactMap
      .filter((col) => col.key.startsWith('Medical Contact'))
      .reduce((map, column) => {
        if (isEmpty(map[column.key])) {
          map[column.key] = [];
        }
        map[column.key].push(column);
        return map;
      }, {});
  }

  @computed('compactMap.[]')
  get medicalConditionMap() {
    return this.compactMap
      .filter((col) => col.key.startsWith('Medical Condition'))
      .reduce((map, column) => {
        if (isEmpty(map[column.key])) {
          map[column.key] = [];
        }
        map[column.key].push(column);
        return map;
      }, {});
  }

  // findPotentialMatch attempts to match an incoming CSV column against a
  // dynamic field or other predefined property/field which can accept data.
  // Returns a TrackedArray, not a plain array: ImportFieldMapper's dropField
  // mutates each column's slot in place (push/splice) via DragSortList, and
  // that needs to be independently reactive.
  findPotentialMatch(personType, key, column, search, extra) {
    let colArray = new TrackedArray();
    // Start by searching for a field whose label matches.
    let camelCased = toCamelCase(column.trim().toLowerCase());
    let found = search.find((field) => {
      return field.camelCaseLabel === camelCased;
    });
    if (isEmpty(found) && isArray(extra) && extra.length) {
      found = extra.find((field) => {
        return field.camelCaseLabel === camelCased;
      });
    }
    if (isEmpty(found)) {
      // Next, try searching for a field in our internal map.
      if (
        alternateNames.hasOwnProperty(personType) &&
        Object.keys(alternateNames[personType]).includes(camelCased)
      ) {
        let camelCasedAlt = toCamelCase(
          alternateNames[personType][camelCased].toLowerCase(),
        );
        found = search.find((field) => {
          return field.camelCaseLabel === camelCasedAlt;
        });
        if (isEmpty(found) && isArray(extra) && extra.length) {
          found = extra.find((field) => {
            return field.camelCaseLabel === camelCasedAlt;
          });
        }
      }
    }
    if (!isEmpty(found)) {
      colArray.push({
        key,
        field: found,
      });
    }
    return colArray;
  }

  @tracked
  seenHeroHeader = false;
  @tracked
  seenHeader = false;
  mappedData = [];
  @tracked
  bulkImport = null;
  @tracked
  importing = false;

  @tracked
  preview;

  @tracked
  skippedContactEmails;

  @action
  checkHeroFile() {
    if (isEmpty(this.file) || !this.isHeroFile) {
      // No file provided yet, or not a Hero file.  No school check possible/required.
      return;
    }

    // Papa Parse is async, so we have to flag that we're checking so that we can prevent moving to the next step.
    this.heroFileCheckInProgress = true;
    this.incorrectSchool = false;

    Papa.parse(this.file.data, {
      header: false,
      preview: 1,
      complete: this.checkSchoolId.bind(this),
    });
  }

  @action
  processColumns() {
    Papa.parse(this.file.data, {
      header: false,
      preview: 11,
      complete: this.columnsProcessed.bind(this),
    });
  }

  @action
  mappingChanged(map) {
    map.forEach((row, index) => {
      // `row` is a POJO.  No `set()` here.
      if (!isEmpty(row)) {
        row.index = index;
      }
    });
    this.columnMap = map;
  }

  @action
  import() {
    this.importing = true;

    // Set base properties of bulk import.
    let source = BulkImportSource.CSV_FILE;
    if (this.isHeroFile) {
      source = BulkImportSource.HERO_FILE;
    }
    let bulkImport = this.store.createRecord('people/bulk-import', {
      schoolId: this.session.activeSchool,
      source,
      description: this.file.name,
    });
    switch (this.importType) {
      case 'staff':
        bulkImport.set('importType', BulkImportType.STAFF);
        break;

      case 'learners':
        bulkImport.set('importType', BulkImportType.LEARNERS);
        break;
    }
    this.bulkImport = bulkImport;

    Papa.parse(this.file.data, {
      header: false,
      step: this.mapRow.bind(this),
      complete: this.importMappedData.bind(this),
    });
  }

  matchExistingLearner(data) {
    let heroId = data[this.heroIdIndex];
    return this.model.people.find(
      (item) => item.heroId === parseInt(heroId, 10),
    );
  }

  // mapRow takes a row of the uploaded CSV/Hero file and maps it into a
  // BulkImportPerson, which it appends to the BulkImport entity.
  mapRow(row) {
    // Skip Hero file header (if applicable).
    if (this.isHeroFile && !this.seenHeroHeader) {
      this.seenHeroHeader = true;
      return;
    }

    // Skip header row.
    if (!this.seenHeader) {
      this.seenHeader = true;
      return;
    }

    // Skip empty rows.
    let data = row.data;
    if (isEmpty(data) || data.every((cell) => isEmpty(cell))) {
      return;
    }

    let frag;

    switch (this.importType) {
      case 'learners':
        // If we don't know where to find the Jurisdictional ID/HeroId, don't even try.
        if (
          this.jurisdictionalIdIndex === false &&
          this.heroIdIndex === false
        ) {
          this.toaster.error('Jurisdictional ID or Hero ID field not mapped.');
          return;
        }

        frag = this.store.createFragment('people/bulk-import-person', {
          type: PersonType.LEARNER,
        });

        // If there is a Hero ID, map by personId
        if (!isEmpty(data[this.heroIdIndex])) {
          let person = this.matchExistingLearner(row.data);
          if (!isEmpty(person)) {
            frag.set('personId', person.id);
          }
        }

        // If there is no Hero ID, map by jurisdictional ID
        if (
          !isEmpty(data[this.jurisdictionalIdIndex]) &&
          isEmpty(data[this.heroIdIndex])
        ) {
          frag.addField(
            this.jurisdictionalIdField.key,
            this.jurisdictionalIdField.dataType,
          );
          frag.set(
            `fields.${this.jurisdictionalIdField.key}.value`,
            data[this.jurisdictionalIdIndex],
          );
        }

        // Map learner fields
        this.mapFields(frag, this.learnerMap, data);

        // Map enrolment fields + properties.
        this.mapEnrolmentData(frag, data);

        // Map contacts/caregivers.
        this.mapContacts(frag, data);

        // Map medical contacts.
        this.mapMedicalContacts(frag, data);

        // Map medical conditions.
        this.mapMedicalConditions(frag, data);

        // Map finance account salutation, if applicable.
        if (this.hasAccountSalutationField) {
          frag.setProperties({
            financeSalutation: data[this.accountSalutationField.index].trim(),
            updateFinanceSalutation: true,
          });
        }
        break;

      case 'staff':
        frag = this.store.createFragment('people/bulk-import-person', {
          type: PersonType.STAFF,
        });
        this.mapFields(frag, this.staffMap, data);

        break;
    }
    if (!isEmpty(frag)) {
      // pull in the schools current learners
      // use the frag details to see if a user exists
      // if it does grab their person id and pass it into the frag
      this.bulkImport.importData.pushObject(frag);
    }
  }

  // mapFields maps import data into dynamic field values, and doesn't care
  // about where those dynamic fields go (Person, Contact, Enrolment, etc).
  mapFields(frag, map, data) {
    let listsHandled = [];
    map.forEach((mapping) => {
      let key = mapping.field.key;
      let value = data[mapping.index].trim();

      let isAddress = key.includes(':address') || key.includes(':postcode');
      // Allow empty address lines and postcode
      // Empty address line 1 is handled in backend to add log warning
      if (!isAddress && value === '') {
        // Skip empty fields - we don't want to import them, as doing so could
        // result in removing a valid value which is already known by Hero.
        return;
      }

      if (mapping.field.dataType === DataType.LIST) {
        // For list fields, skip if we've already processed this field.
        if (listsHandled.includes(key)) {
          return;
        }
        let extra = rejectBy(
          filterBy(map, 'field.key', key),
          'index',
          mapping.index,
        );
        extra.forEach((m) => {
          let extraValue = data[m.index].trim();
          if (!isEmpty(extraValue)) {
            value = [value, extraValue].join(',');
          }
        });
        listsHandled.push(key);
      }

      frag.addField(key, mapping.field.dataType);
      let field = frag.get(`fields.${key}`);
      field.set('value', conformFieldValue(mapping.field, value));
    });
  }

  // mapEnrolmentData maps import data into Enrolment-related BulkImportPerson
  // properties (and "meta" dynamic fields, if applicable).
  mapEnrolmentData(frag, data) {
    let propsToImport = [];
    // NOTE: FTE is processed as a dynamic field rather than using the "fte"
    // property on the Enrolment record.
    if (!isEmpty(this.startDateField)) {
      frag.set(
        'entryDate',
        this.conformDateForImport(data[this.startDateField.index]),
      );
      propsToImport.push(BulkImportEnrolmentProp.ENTRY_DATE);
    }
    if (!isEmpty(this.endDateField)) {
      frag.set(
        'exitDate',
        this.conformDateForImport(data[this.endDateField.index]),
      );
      propsToImport.push(BulkImportEnrolmentProp.EXIT_DATE);
    }

    // Process former or pre-enrolled status if we have the column(s).
    if (this.formerIndex !== false && isTruthy(data[this.formerIndex])) {
      frag.set('status', EnrolmentStatus.FORMER);
    } else if (
      this.preEnrolIndex !== false &&
      isTruthy(data[this.preEnrolIndex])
    ) {
      frag.set('status', EnrolmentStatus.PRE_ENROLLED);
    } else if (this.hasStatusColumn) {
      // We have at least one status column, but not a definitive status.
      // Should therefore be current (neither former nor pre-enrolled).
      frag.set('status', EnrolmentStatus.CURRENT);
    }

    frag.set('enrolmentPropsToImport', propsToImport);

    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        this.mapEnrolmentMetaNzMoe(frag, data);
        break;
    }
  }

  // mapEnrolmentMetaNzMoe maps import data into NZ MoE enrolment meta fields,
  // and maps departure reasons to the NZ MoE code set.
  mapEnrolmentMetaNzMoe(frag, data) {
    // Reason for Leaving sits directly in the BulkImportPerson.
    let reasonField = this.enrolmentMap.find(
      (item) => item.field.key === 'reasonForLeaving',
    );
    if (!isEmpty(reasonField)) {
      let reason = this.findEnumValue(
        NzMoeDepartureReason.values,
        data[reasonField.index],
      );
      frag.set('departureReason', reason);
      frag.enrolmentPropsToImport.pushObject(
        BulkImportEnrolmentProp.DEPARTURE_REASON,
      );
    }

    // Destination School and Post-School Activity are meta fields.
    let destinationSchoolField = this.enrolmentMap.find(
      (item) => item.field.key === 'destinationSchool',
    );
    let activityField = this.enrolmentMap.find(
      (item) => item.field.key === 'postSchoolActivity',
    );
    if (!isEmpty(destinationSchoolField) || !isEmpty(activityField)) {
      // Initialise meta fields.
      frag.set('meta', {});

      if (!isEmpty(destinationSchoolField)) {
        let destinationSchool = this.findEnumValue(
          NzMoeSchool.values,
          data[destinationSchoolField.index],
        );
        frag.meta.addField('nzMOE:destinationSchool', DataType.STRING);
        frag.meta
          .get('fields.nzMOE:destinationSchool')
          .set('value', destinationSchool);
      }
      if (!isEmpty(activityField)) {
        let activity = this.findEnumValue(
          NzMoeActivity.values,
          data[activityField.index],
        );
        frag.meta.addField('nzMOE:postSchoolActivity', DataType.STRING);
        frag.meta.get('fields.nzMOE:postSchoolActivity').set('value', activity);
      }
    }
  }

  // mapContacts maps the contact fields for an import row into
  // BulkImportContacts, pushing them into the supplied BulkImportPerson's
  // Contacts property.
  mapContacts(frag, data) {
    let keys = Object.keys(this.contactMap);
    if (isEmpty(keys)) {
      // No contact fields mapped; skip.
      return;
    }

    // Iterate over the mapped contacts
    Object.entries(this.contactMap).forEach(([key, map]) => {
      // Skip completely empty contacts.
      let contactPresent = map.some((mapped) => {
        return !isEmpty(data[mapped.index].trim());
      });
      if (!contactPresent) {
        return;
      }

      // Split the map into dynamic fields and properties.
      let { caregiver, relationship, props } = map.reduce(
        (split, field) => {
          if (get(field, 'field.key').indexOf(':') === -1) {
            split.props.push(field);
          } else if (get(field, 'field.group.name') === 'Relationship') {
            split.relationship.push(field);
          } else {
            split.caregiver.push(field);
          }
          return split;
        },
        { caregiver: [], relationship: [], props: [] },
      );

      // Skip if we're not going to be able to identify the caregiver.
      let firstNameField = caregiver.find(
        (item) => item.field.key === 'core:firstName',
      );
      let lastNameField = caregiver.find(
        (item) => item.field.key === 'core:lastName',
      );
      if (isEmpty(firstNameField) || isEmpty(lastNameField)) {
        // We don't have enough details to identify a caregiver.  Skip.
        return;
      }

      let firstName = data[firstNameField.index].trim();
      let lastName = data[lastNameField.index].trim();
      if (isEmpty(firstName) && isEmpty(lastName)) {
        // We don't have a name by which to identify a caregiver.  Skip.
        return;
      }

      // We have some identifying information.  Build up a Bulk Import Contact.
      let contact = this.store.createFragment('people/bulk-import-contact', {
        type: ContactType.PRIMARY,
        caregiver: {},
      });

      if (emergencyContactFieldPattern.test(key)) {
        contact.set('type', ContactType.EMERGENCY);
      }

      // Process contact properties.
      let propsToImport = [];
      let relationshipIndex = -1;
      props.forEach(({ field, index }) => {
        let fieldKey = field.key;
        switch (fieldKey) {
          case 'contactNotes':
            fieldKey = 'notes';
            break;
          case 'financeAccess':
            fieldKey = 'billPayer';
            break;
          case 'relationship':
            relationshipIndex = index;
            break;
        }
        contact.set(fieldKey, conformFieldValue(field, data[index]));
        propsToImport.push(contactProps[field.key]);
      });

      // Parse/map relationship names to enum values, if applicable.
      if (relationshipIndex > -1) {
        let contactData = {
          contactNotes: contact.notes || '',
          relationship: data[relationshipIndex],
        };
        contactData = this.processContactRelationship(
          contactData,
          Relationship,
        );
        contact.setProperties({
          notes: contactData.contactNotes,
          relationship: contactData.relationship,
        });
        if (
          !isEmpty(contact.notes.trim()) &&
          !propsToImport.includes(BulkImportContactProp.NOTES)
        ) {
          propsToImport.push(BulkImportContactProp.NOTES);
        }
      }

      // Map caregiver and relationship fields into the Bulk Import Contact.
      this.mapFields(contact.caregiver, caregiver, data);
      this.mapFields(contact, relationship, data);
      // A priority of 0 (the Go zero value) is not set.
      if (!contact.priority) {
        // No priority provided.  Use the contact number as the priority.
        let priority = key.match(this.contactPriorityPattern);
        if (!isEmpty(priority) && !isEmpty(priority[1])) {
          contact.set('priority', priority[1]);
        }
      }
      contact.set('propsToImport', propsToImport);
      frag.contacts.pushObject(contact);
    });
  }

  mapMedicalContacts(frag, data) {
    let keys = Object.keys(this.medicalContactMap);
    if (isEmpty(keys)) {
      // No medical contact fields mapped; skip.
      return;
    }

    // Iterate over the mapped medical contacts.
    Object.entries(this.medicalContactMap).forEach(([, map]) => {
      // Skip completely empty contacts.
      let contactPresent = map.some((mapped) => {
        return !isEmpty(data[mapped.index].trim());
      });
      if (!contactPresent) {
        return;
      }

      // Split the map into dynamic fields and properties.
      let { medical, relationship, props } = map.reduce(
        (split, field) => {
          if (get(field, 'field.key').indexOf(':') === -1) {
            split.props.push(field);
          } else if (get(field, 'field.group.name') === 'Relationship') {
            split.relationship.push(field);
          } else {
            split.medical.push(field);
          }
          return split;
        },
        { medical: [], relationship: [], props: [] },
      );

      // We have some identifying information.
      // Build up a Bulk Import Medical Contact.
      let contact = this.store.createFragment(
        'people/bulk-import-medical-contact',
        {
          medical: {},
        },
      );

      let propsToImport = [];
      let relationshipIndex = -1;
      props.forEach(({ field, index }) => {
        if (field.key === 'relationship') {
          relationshipIndex = index;
        }
        let val = data[index];
        if (medicalDateProperties.includes(field.key)) {
          val = this.conformDateForImport(val);
        }
        contact.set(field.key, conformFieldValue(field, val));
        propsToImport.push(contactProps[field.key]);
      });

      // Parse/map relationship names to enum values, if applicable.
      if (relationshipIndex > -1) {
        let contactData = {
          contactNotes: contact.notes || '',
          relationship: data[relationshipIndex],
        };
        contactData = this.processContactRelationship(
          contactData,
          MedicalRelationship,
        );
        contact.setProperties({
          notes: contactData.contactNotes,
          relationship: contactData.relationship,
        });
        if (
          !isEmpty(contact.notes.trim()) &&
          !propsToImport.includes(BulkImportContactProp.NOTES)
        ) {
          propsToImport.push(BulkImportContactProp.NOTES);
        }
      }

      this.mapFields(contact.medical, medical, data);
      this.mapFields(contact, relationship, data);
      contact.set('propsToImport', propsToImport);
      frag.medicalContacts.pushObject(contact);
    });
  }

  mapMedicalConditions(frag, data) {
    let keys = Object.keys(this.medicalConditionMap);
    if (isEmpty(keys)) {
      // No medical condition fields mapped; skip.
      return;
    }

    // Iterate over the mapped medical conditions.
    Object.entries(this.medicalConditionMap).forEach(([, map]) => {
      // Skip completely empty conditions.
      let conditionPresent = map.some((mapped) => {
        return !isEmpty(data[mapped.index].trim());
      });
      if (!conditionPresent) {
        return;
      }

      // Split the map into dynamic fields and properties.
      let { fields, props } = map.reduce(
        (split, field) => {
          if (get(field, 'field.key').indexOf(':') === -1) {
            split.props.push(field);
          } else {
            split.fields.push(field);
          }
          return split;
        },
        { fields: [], props: [] },
      );

      // We have some medical condition details.
      // Build up a Medical Condition and add to the Bulk Import.
      let condition = this.store.createFragment('people/medical-condition', {});

      props.forEach(({ field, index }) => {
        let val = data[index];
        if (medicalDateProperties.includes(field.key)) {
          val = this.conformDateForImport(val);
        }
        condition.set(field.key, conformFieldValue(field, val));
      });

      this.mapFields(condition, fields, data);
      frag.medicalConditions.pushObject(condition);
    });
  }

  // importMappedData sends the mapped import data to the back end for
  // processing, and schedules the first status check.
  importMappedData() {
    this.bulkImport.save();

    later(this, 'checkImportStatus', 2000);
  }

  // checkImportStatus checks the status of an in-flight Bulk Import.
  checkImportStatus() {
    this.bulkImport.reload().then(() => {
      if (this.bulkImport.status === BulkImportStatus.PROCESSING) {
        later(this, 'checkImportStatus', 2000);
      } else {
        this.importing = false;
      }
    });
  }

  // findEnumValue maps incoming data to enum values, by either value or label.
  findEnumValue(values, query) {
    let value = values.find((item) => item.value === query.trim());
    if (!isEmpty(value)) {
      return value.value;
    }
    let camelCased = toCamelCase(query.trim());
    value = values.find((val) => {
      return toCamelCase(val.label.trim()) === camelCased;
    });
    if (!isEmpty(value)) {
      return value.value;
    }
  }

  // processContactRelationship preprocesses a contact's "relationship" to
  // match it to a predefined relationship type, pushing unknown values into
  // the contact notes so that we don't lose information.
  processContactRelationship(data, relationshipEnum) {
    // Find value in the enum, or default to Other if not found.
    let value = data.relationship;
    if (Number.isInteger(value)) {
      // If the value is already an integer, we've already processed it.  Don't try to do so again.
      return data;
    }
    if (isEmpty(value)) {
      // If no relationship was provided, set it to "other" so that we have a value to work with.
      value = 'Other';
    }

    value =
      relationshipEnum[this.conformEnumName(value)] || relationshipEnum.OTHER;
    if (
      value === relationshipEnum.OTHER &&
      data.relationship.toLowerCase().trim() !== 'other'
    ) {
      if (data.hasOwnProperty('contactNotes') && data.contactNotes.length > 0) {
        // We already have notes - prepend if necessary.
        if (data.contactNotes.indexOf(data.relationship) !== 0) {
          // Notes don't already start with the relationship type; prepend it.
          data.contactNotes = `${data.relationship} - ${data.contactNotes}`;
        }
      } else {
        // No notes at present - just set the notes to the raw relationship type from the uploaded data.
        data.contactNotes = data.relationship;
      }
    }
    data.relationship = value;

    return data;
  }

  // conformEnumName conforms text to SHOUTING_SNAKE_CASE for use in enums.
  conformEnumName(name) {
    return name.toUpperCase().replace(/[^A-Z0-9_]/g, '_');
  }

  // conformDateForImport attempts to parse/conform dates into the YYYY-MM-DD
  // format which the back end understands, but returns the original value if
  // it's invalid so that the back end is able to generate a warning.
  conformDateForImport(rawDate) {
    let conformed = conformDate(rawDate);
    if (isEmpty(conformed) && !isEmpty(rawDate)) {
      // Date was invalid / unable to be parsed.  Send the bad value,
      // so that the back end can generate a validation warning.
      return rawDate;
    }
    return conformed;
  }

  // reset resets all of the import-related properties so that another import
  // can be performed cleanly (if desired).
  @action
  reset() {
    this.currentStep = 0;
    this.file = null;
    this.processingColumns = false;
    this.preview = null;
    this.columnMap = [];
    this.importType = 'learners';
    this.seenHeader = false;
    this.seenHeroHeader = false;
    this.incorrectSchool = false;
    this.heroFileCheckInProgress = false;
    this.heroFileLearnersWithoutIdCreated = [];
    this.skippedContactEmails = [];
    this.bulkImport = null;
  }
}
